import { useMemo } from "react";

import {
  Badge,
  ErrorPage,
  FontIcon,
  PageLayout,
  TableColumnDefinition,
  TableRowDefinition,
  TableWithDefaultFilters,
  Typography,
} from "@ezmid/components-library";
import {
  faDatabase,
  faFingerprint,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useGetBackOfficeListProductsQuery } from "../../api/products.generated";
import { useFormatters } from "../../hooks";
import { makeRoute } from "../../hooks/route";
import { AllRoutes } from "../route/Route";

type TableData = {
  description: string;
  priceLevel: number;
  vendorProductID: string;
  googleVendorProductID: string;
  createdAt: string;
};

export function ProductsPage<T extends object>() {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const navigate = useNavigate();

  const { currentData, isError } = useGetBackOfficeListProductsQuery({
    size: 1000,
  });

  const memoizedData: Array<TableData> = useMemo(
    () =>
      currentData?.items?.map((product) => {
        return {
          description: product?.description || "",
          priceLevel: product?.priceLevel || 0,
          vendorProductID: product?.vendorProductID || "",
          googleVendorProductID: product?.googleVendorProductID || "",
          createdAt: product?.createdAt || "",
          id: product?.id || "",
        };
      }) || [],
    [currentData]
  );

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.description"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          return (
            <Typography variant="body1">{row?.original.description}</Typography>
          );
        },
        id: "description",
        accessor: "description",
      },
      {
        Header: t("table.header.vendorProductID"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <div className="min-w-0 break-all [&_*]:!whitespace-normal">
            <Badge
              variant="pill"
              leftIcon={
                <FontIcon
                  marginTop={4}
                  customIcon={
                    <FontAwesomeIcon size="lg" icon={faFingerprint} />
                  }
                />
              }
            >
              {row?.original.vendorProductID}
            </Badge>
          </div>
        ),
        id: "vendorProductID",
        accessor: "vendorProductID",
      },
      {
        Header: t("table.header.googleVendorProductID"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <div className="min-w-0 break-all [&_*]:!whitespace-normal">
            <Badge
              variant="pill"
              leftIcon={
                <FontIcon
                  marginTop={4}
                  customIcon={
                    <FontAwesomeIcon size="lg" icon={faFingerprint} />
                  }
                />
              }
            >
              {row?.original.googleVendorProductID}
            </Badge>
          </div>
        ),
        id: "googleVendorProductID",
        accessor: "googleVendorProductID",
      },
      {
        Header: t("table.header.priceLevel"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            status="break"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faDatabase} />}
              />
            }
          >
            {row?.original.priceLevel ?? ""}
          </Badge>
        ),
        id: "numberOfContents",
        accessor: "numberOfContents",
      },
      {
        Header: t("table.header.createdAt"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span>{formatDate(row?.original.createdAt)}</span>
        ),
        id: "createdAt",
        accessor: "createdAt",
      },
    ],
    []
  );

  return isError ? (
    <ErrorPage
      errorMessageText={t("app.page.error.page.not.found")}
      messageForUser={t("app.page.error.defaultMessage")}
      hasGoBackButton
      buttonText={t("app.goToDasboard")}
      onButtonClick={() => navigate(makeRoute(AllRoutes.DASHBOARD))}
    />
  ) : (
    <PageLayout title={t("app.page.products.heading")}>
      <div className="w-full min-w-full p-2">
        <TableWithDefaultFilters
          headerIcon={
            <FontIcon
              marginLeft={4}
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faList} />}
            />
          }
          headerText={t("card.header.list")}
          tableData={memoizedData}
          tableColumns={columns}
        />
      </div>
    </PageLayout>
  );
}
